import { useState } from 'react';
import { isEqual } from 'lodash-es';
import { useDebounceCallback } from '@/hooks/state/use-debounce-callback';

/** Condition builders emit on every edit, so a commit waits for the reader to settle. */
const FILTER_COMMIT_DEBOUNCE_MS = 400;

/** Whether a list is narrowed by its free-text search or by a condition builder. */
export type ListSearchBy = 'search' | 'conditions';

export type ListPickerParams = {
  /** The free-text term: an email address for people, a name for objects. */
  search: string;
  /** Encoded conditions, or null when none are applied. */
  filters: string | null;
  page: number;
};

/** Translates between the stored `filters` param and the tree a builder emits. */
export type ListFilterCodec<Wire> = {
  /** The builder's tree for an encoded param, used to recognise an echo. */
  decode: (encoded: string | null) => Wire;
  /** The encoded param for a tree, or null when the tree cannot be encoded. */
  encode: (serialized: Wire) => string | null;
};

type ListPickerOptions<Item, Wire> = {
  initialSelected: readonly Item[];
  codec: ListFilterCodec<Wire>;
};

export type ListPicker<Item, Wire> = {
  params: ListPickerParams;
  searchBy: ListSearchBy;
  selected: readonly Item[];
  setSelected: (items: readonly Item[]) => void;
  setPage: (page: number) => void;
  search: (term: string) => void;
  applyFilters: (serialized: Wire, isValid: boolean, isEmpty: boolean) => void;
  clearFilters: () => void;
  activateSearchBy: (next: ListSearchBy) => void;
};

/**
 * One picker list inside the relationships wizard: which page and narrowing are
 * in effect, and which records are chosen.
 *
 * Narrowing the list drops the selection, because a row the reader can no
 * longer see would still be submitted. The one exception is editing a condition
 * that stays non-empty, which leaves the selection alone.
 *
 * State lives here rather than in the step component because a tab panel
 * unmounts when the reader steps away, and the wizard has to come back to the
 * same list.
 */
export function useListPicker<Item, Wire>({
  initialSelected,
  codec,
}: ListPickerOptions<Item, Wire>): ListPicker<Item, Wire> {
  const [params, setParams] = useState<ListPickerParams>({ search: '', filters: null, page: 1 });
  const [searchByChoice, setSearchByChoice] = useState<ListSearchBy>('search');
  const [selected, setSelected] = useState<readonly Item[]>(initialSelected);
  // An applied condition set pins the mode, so clearing it cannot strand the
  // reader on a search box that is filtering nothing.
  const searchBy: ListSearchBy = params.filters == null ? searchByChoice : 'conditions';

  const commitFilters = useDebounceCallback((serialized: Wire, isEmpty: boolean) => {
    if (isEqual(serialized, codec.decode(params.filters))) return;
    if (isEmpty) {
      setParams({ search: '', filters: null, page: 1 });
      setSelected([]);
      return;
    }
    const encoded = codec.encode(serialized);
    if (encoded == null) return;
    setParams((current) => ({ ...current, filters: encoded, page: 1 }));
  }, FILTER_COMMIT_DEBOUNCE_MS);

  const reset = (next: Partial<ListPickerParams>) => {
    commitFilters.cancel();
    setParams((current) => ({ ...current, ...next }));
    setSelected([]);
  };

  return {
    params,
    searchBy,
    selected,
    setSelected,
    setPage: (page) => reset({ page }),
    search: (term) => reset({ search: term, page: 1 }),
    clearFilters: () => reset({ search: '', filters: null, page: 1 }),
    applyFilters: (serialized, isValid, isEmpty) => {
      commitFilters.cancel();
      if (isValid || isEmpty) commitFilters(serialized, isEmpty);
    },
    activateSearchBy: (next) => {
      if (next === searchBy) return;
      setSearchByChoice(next);
      reset({ search: '', filters: null, page: 1 });
    },
  };
}
